import { useRef, useState } from "react";

import type { ComposerUsageNoticeContextIdentity } from "./composerUsageNoticeContext";

interface ComposerUsageNoticeState {
  readonly identity: ComposerUsageNoticeContextIdentity | null;
  readonly open: boolean;
}

export function useComposerUsageNoticeState(
  identity: ComposerUsageNoticeContextIdentity | null,
  threadId: string,
): {
  readonly isOpen: boolean;
  readonly open: () => boolean;
  readonly close: () => void;
  readonly clearForAcceptedTurn: (acceptedThreadId: string) => void;
} {
  const [state, setState] = useState<ComposerUsageNoticeState>({ identity, open: false });
  if (!noticeStateIdentityMatches(state.identity, identity)) {
    setState({
      identity,
      open: shouldKeepOpenThroughInitialAccountResolution(state, identity),
    });
  }
  const isOpen = noticeStateIdentityMatches(state.identity, identity) && state.open;
  const currentThreadIdRef = useRef(threadId);
  currentThreadIdRef.current = threadId;
  return {
    isOpen,
    open: () => {
      if (!identity) return false;
      setState({ identity, open: true });
      return true;
    },
    close: () => setState({ identity, open: false }),
    clearForAcceptedTurn: (acceptedThreadId) => {
      if (currentThreadIdRef.current === acceptedThreadId) setState({ identity, open: false });
    },
  };
}

function noticeStateIdentityMatches(
  left: ComposerUsageNoticeContextIdentity | null,
  right: ComposerUsageNoticeContextIdentity | null,
): boolean {
  return left?.key === right?.key && left?.accountResolved === right?.accountResolved;
}

function shouldKeepOpenThroughInitialAccountResolution(
  state: ComposerUsageNoticeState,
  nextIdentity: ComposerUsageNoticeContextIdentity | null,
): boolean {
  return Boolean(
    state.open &&
    state.identity &&
    nextIdentity &&
    state.identity.baseKey === nextIdentity.baseKey &&
    !state.identity.accountResolved &&
    nextIdentity.accountResolved &&
    state.identity.selectedAccountId === nextIdentity.selectedAccountId,
  );
}
